import { PlanSelector } from "@tento-ui/brand/plan-selector/plan-selector";

export namespace PublicPlanCopy {
  export type PlanId = "Personal" | "Pro" | "Teams" | "Trial";

  export type ViewModel = {
    label: string;
    labelTag: Option<string>;
    caption: string;
    plan: PlanId;
    planTier: number;
    priceUsdPerMth: Option<number>;
    priceUsdPerMthAnnualDiscount: Option<number>;
    features: string[];
  };

  export type Copy = {
    title: string;
    caption: string;
    tag: Option<string>;
    features: string[];
  };

  export function planTier(e: PlanId): number {
    switch (e) {
      case "Trial":
        return 0;
      case "Personal":
        return 1;
      case "Pro":
        return 2;
      case "Teams":
        return 3;
    }
  }

  // Tiers split on whose money it is, not headcount: Personal is your own
  // life on your own wallet; Professional is for people who bill their
  // time (availability links self-identify work use); Teams coordinates
  // other people. Prices stay round — annual saves 20% or more.
  export const ALL: ViewModel[] = [
    {
      label: "Personal",
      labelTag: null,
      caption: "Your week, your way — for planning your own life",
      plan: "Personal",
      planTier: 1,
      priceUsdPerMth: 9,
      priceUsdPerMthAnnualDiscount: 7,
      features: [
        "Calendar, tasks, and notes in one weekly view",
        "Stickers for travel, recovery, focus, and deadlines",
        "Daily planning that starts from real events",
      ],
    },
    {
      label: "Professional",
      labelTag: "Most popular",
      caption: "For people who bill their time — share availability, plan around your energy",
      plan: "Pro",
      planTier: 2,
      priceUsdPerMth: 19,
      priceUsdPerMthAnnualDiscount: 15,
      features: [
        "Everything in Personal",
        "Availability links that reflect your real week — slots update live from your calendar",
        "Plan around your energy with Whoop and Garmin",
        "Booking receipts and calendar invites for your guests",
      ],
    },
    {
      label: "Teams",
      labelTag: null,
      caption: "For teams coordinating time, handoffs, and shared plans",
      plan: "Teams",
      planTier: 3,
      priceUsdPerMth: 19.99,
      priceUsdPerMthAnnualDiscount: 15.99,
      features: [
        "Everything in Professional, per seat",
        "Shared planning surfaces for calendars and sheets",
        "Team availability and scheduling workflows",
      ],
    },
    {
      label: "Contact Us",
      labelTag: null,
      caption: "Need a custom rollout or workflow? We can help",
      plan: "Trial",
      planTier: 0,
      priceUsdPerMth: null,
      priceUsdPerMthAnnualDiscount: null,
      features: [
        "Calendar-led workflow design",
        "Workspace rollout support",
        "Dedicated implementation guidance",
      ],
    },
  ];

  export function toPlanSelectorPlans(): PlanSelector.Plan[] {
    return ALL.map((plan) => ({
      id: plan.plan,
      title: plan.label,
      caption: plan.caption,
      tag: plan.labelTag,
      tier: plan.planTier,
      priceUsdPerMonth: plan.priceUsdPerMth,
      priceUsdPerMonthAnnual: plan.priceUsdPerMthAnnualDiscount,
      features: plan.features,
    }));
  }

  export const PLANS: PlanSelector.Plan[] = toPlanSelectorPlans();

  export const COPY: Record<string, Copy> = makeCopyByCatalogId();

  function makeCopyByCatalogId(): Record<string, Copy> {
    const copy: Record<string, Copy> = {};
    for (const plan of ALL) {
      copy[catalogId(plan.plan)] = {
        title: plan.label,
        caption: plan.caption,
        tag: plan.labelTag,
        features: plan.features,
      };
    }
    return copy;
  }

  function catalogId(plan: PlanId): string {
    return plan === "Trial" ? "ContactUs" : plan;
  }
}
